import {
  HttpBackend,
  HttpClient,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY, expand, map, reduce, switchMap } from 'rxjs';
import { environment } from '@env/environment';
import type {
  AppStoreApplication,
  AppStoreArtifact,
  AppStorePage,
  AppStoreRelease,
  CreateAppStoreApplication,
  CreateAppStoreRelease,
  UploadAppStoreArtifact,
} from '../../dom/models/app-store.models';
import { AppStoreRepository } from '../../dom/ports/app-store.repository';

interface UploadUrl {
  url: string;
  headers: Record<string, string>;
  expiresAt: string;
}

interface ArtifactUploadPreparation {
  artifact: AppStoreArtifact;
  upload: UploadUrl;
}

const PAGE_SIZE = 100;

@Injectable()
export class AppStoreHttpRepository implements AppStoreRepository {
  private readonly http = inject(HttpClient);
  // Presigned R2 URLs must not pass through SSO/auth interceptors.
  private readonly signedUploadHttp = new HttpClient(inject(HttpBackend));
  private readonly baseUrl = environment.appStoreApiUrl;

  getApplications() {
    return this.getAllPages<AppStoreApplication>(this.baseUrl + '/apps');
  }

  createApplication(payload: CreateAppStoreApplication) {
    return this.http.post<AppStoreApplication>(this.baseUrl + '/apps', payload);
  }

  updateApplication(applicationId: string, payload: CreateAppStoreApplication) {
    return this.http.patch<AppStoreApplication>(
      this.baseUrl + '/apps/' + encodeURIComponent(applicationId),
      payload,
    );
  }

  deleteApplication(applicationId: string) {
    return this.http.delete<void>(this.baseUrl + '/apps/' + encodeURIComponent(applicationId));
  }

  deleteRelease(applicationId: string, releaseId: string) {
    return this.http.delete<void>(
      this.baseUrl + '/apps/' + encodeURIComponent(applicationId) + '/releases/' + encodeURIComponent(releaseId),
    );
  }

  deleteArtifact(applicationId: string, releaseId: string, artifactId: string) {
    return this.http.delete<void>(this.artifactsUrl(applicationId, releaseId) + '/' + encodeURIComponent(artifactId));
  }

  getReleases(applicationId: string) {
    return this.getAllPages<AppStoreRelease>(
      this.baseUrl + '/apps/' + encodeURIComponent(applicationId) + '/releases',
    );
  }

  createRelease(applicationId: string, payload: CreateAppStoreRelease) {
    return this.http.post<AppStoreRelease>(
      this.baseUrl + '/apps/' + encodeURIComponent(applicationId) + '/releases',
      payload,
    );
  }

  updateRelease(
    applicationId: string,
    releaseId: string,
    payload: CreateAppStoreRelease,
  ) {
    return this.http.patch<AppStoreRelease>(
      this.baseUrl +
        '/apps/' +
        encodeURIComponent(applicationId) +
        '/releases/' +
        encodeURIComponent(releaseId),
      payload,
    );
  }

  getArtifacts(applicationId: string, releaseId: string) {
    return this.http.get<AppStoreArtifact[]>(this.artifactsUrl(applicationId, releaseId));
  }

  uploadAndPublishArtifact(input: UploadAppStoreArtifact) {
    const collectionUrl = this.artifactsUrl(input.applicationId, input.releaseId);
    const contentType = input.file.type.trim() || 'application/octet-stream';

    return this.http
      .post<ArtifactUploadPreparation>(collectionUrl + '/upload-url', {
        platform: input.platform,
        buildNumber: input.buildNumber,
        fileName: input.fileName,
        contentType,
        expectedSizeBytes: input.file.size,
        sha256: input.sha256,
        minOsVersion: input.minOsVersion || undefined,
      })
      .pipe(
        switchMap(({ artifact, upload }) =>
          this.signedUploadHttp
            .put(upload.url, input.file, {
              headers: new HttpHeaders(upload.headers),
              responseType: 'text',
            })
            .pipe(map(() => artifact)),
        ),
        switchMap((artifact) =>
          this.http.post<AppStoreArtifact>(
            collectionUrl + '/' + encodeURIComponent(artifact.id) + '/publish',
            {},
          ),
        ),
      );
  }

  private getAllPages<T>(url: string) {
    const loadPage = (page: number) =>
      this.http.get<AppStorePage<T>>(url, {
        params: new HttpParams().set('page', page).set('limit', PAGE_SIZE),
      });

    return loadPage(1).pipe(
      expand((result) =>
        result.page * result.limit < result.total
          ? loadPage(result.page + 1)
          : EMPTY,
      ),
      reduce((items, result) => items.concat(result.items), [] as T[]),
    );
  }

  private artifactsUrl(applicationId: string, releaseId: string) {
    return (
      this.baseUrl +
      '/apps/' +
      encodeURIComponent(applicationId) +
      '/releases/' +
      encodeURIComponent(releaseId) +
      '/artifacts'
    );
  }
}
